import { useMemo, useContext, createContext, useCallback, useEffect } from 'react'
import { useDispatch, useSelector, useSelectorWithRef } from '../../../redux/hooks'
import planningRecordsActions from '../../../redux/modules/planningRecords/actions'
import planningRecordsSelectors from '../../../redux/modules/planningRecords/selectors'
import { useStateWithRef } from '../../../hooks/shared/useStateWithRef'
import { useUnsubscribe } from '../../../hooks/shared/useUnsubscribe'
import planningRecordService from '../../../services/planningRecordService'
import { PlanningRecord } from '@leadgen/models/client'
import { useRegisteredComponents } from '../../../hooks/shared/useRegisteredComponents'
import useStableCallback from '../../../hooks/shared/useStableCallback'

export type PlanningRecordsStatus =
  | 'reloading-initial'
  | 'loading-initial'
  | 'loading-more'
  | 'idle'
  | 'empty'
  | 'error-initial'
  | 'error-more'

const PlanningRecordsContext = createContext<{
  registerComponent: () => () => void
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  loadFresh: () => void
  loadMore: () => void
  status: PlanningRecordsStatus
  planningRecords: PlanningRecord[]
  error: string | undefined
} | null>(null)

const usePlanningRecords = (
  { skipRegistration }: { skipRegistration: boolean } = { skipRegistration: false },
) => {
  const context = useContext(PlanningRecordsContext)
  if (!context) {
    throw new Error('no PlanningRecordsProvider found when calling usePlanningRecords()')
  }
  const {
    registerComponent,
    loadFresh,
    loadMore,
    hasMore,
    loading,
    loadingMore,
    status,
    planningRecords,
    error,
  } = context
  useEffect(() => {
    if (!skipRegistration) {
      return registerComponent()
    }
  }, [registerComponent, skipRegistration])
  return {
    loadFresh,
    loadMore,
    hasMore,
    loading,
    loadingMore,
    status,
    planningRecords,
    error,
  }
}

const usePlanningRecordsPreview = (
  { skipRegistration }: { skipRegistration: boolean } = { skipRegistration: false },
) => {
  const context = useContext(PlanningRecordsContext)
  if (!context) {
    throw new Error('no PlanningRecordsProvider found when calling usePlanningRecords()')
  }
  const { registerComponent, loading, status, planningRecords, error } = context
  const topThreePlanningRecords = useMemo(() => {
    return planningRecords?.slice(0, 3)
  }, [planningRecords])
  useEffect(() => {
    if (!skipRegistration) {
      return registerComponent()
    }
  }, [registerComponent, skipRegistration])
  return {
    planningRecords: topThreePlanningRecords,
    status,
    loading,
    error,
  }
}

const PlanningRecordsProvider = ({ children }) => {
  const dispatch = useDispatch()
  const { unsubscribe, registerUnsubscribe } = useUnsubscribe()
  const { registerComponent, hasRegisteredComponents, registeredComponentsRef } =
    useRegisteredComponents()
  const [hasMore, setHasMore, hasMoreRef] = useStateWithRef(false)
  const planningRecords = useSelector(planningRecordsSelectors.getPlanningRecords)
  const [loading, loadingRef] = useSelectorWithRef(planningRecordsSelectors.getLoading())
  const [loadingMore, loadingMoreRef] = useSelectorWithRef(planningRecordsSelectors.getLoadingMore)
  const [, initialRef] = useSelectorWithRef(planningRecordsSelectors.getInitial)
  const error = useSelector(planningRecordsSelectors.getError)
  const status: PlanningRecordsStatus =
    loading && planningRecords.length
      ? 'reloading-initial'
      : loading
        ? 'loading-initial'
        : loadingMore
          ? 'loading-more'
          : !loading && !loadingMore && error && !planningRecords.length
            ? 'error-initial'
            : !loading && !loadingMore && error && planningRecords.length
              ? 'error-more'
              : !loading && !loadingMore && !error && !planningRecords.length
                ? 'empty'
                : 'idle'
  const loadFresh = useCallback(() => {
    if (
      hasRegisteredComponents &&
      ((!loadingRef.current && !loadingMoreRef.current) || initialRef.current)
    ) {
      dispatch(planningRecordsActions.loadPlanningRecordsFresh())
        .unwrap()
        .then(({ data, unsubscribe: unsubscribeFromPlanningRecords }) => {
          if (!registeredComponentsRef.current.length) {
            unsubscribeFromPlanningRecords()
            unsubscribe()
          } else {
            setHasMore(data?.length === planningRecordService.pageSize)
            unsubscribe()
            registerUnsubscribe(unsubscribeFromPlanningRecords)
          }
        })
    }
  }, [
    dispatch,
    hasRegisteredComponents,
    initialRef,
    loadingRef,
    loadingMoreRef,
    registerUnsubscribe,
    registeredComponentsRef,
    setHasMore,
    unsubscribe,
  ])
  const loadMore = useCallback(() => {
    if (
      hasMoreRef.current &&
      ((!loadingRef.current && !loadingMoreRef.current) || initialRef.current)
    ) {
      dispatch(planningRecordsActions.loadPlanningRecordsNext())
        .unwrap()
        .then(({ data, unsubscribe: unsubscribeFromPlanningRecords }) => {
          if (!registeredComponentsRef.current.length) {
            unsubscribeFromPlanningRecords()
            unsubscribe()
          } else {
            setHasMore(data?.length === planningRecordService.pageSize)
            registerUnsubscribe(unsubscribeFromPlanningRecords)
          }
        })
    }
  }, [
    dispatch,
    hasMoreRef,
    initialRef,
    loadingRef,
    loadingMoreRef,
    registerUnsubscribe,
    registeredComponentsRef,
    setHasMore,
    unsubscribe,
  ])
  useEffect(() => {
    loadFresh()
  }, [loadFresh])
  useEffect(() => {
    if (!hasRegisteredComponents) {
      unsubscribe()
      return
    }
  }, [hasRegisteredComponents, unsubscribe])
  useEffect(() => {
    return () => {
      unsubscribe()
    }
  }, [unsubscribe])
  const loadFreshHandler = useStableCallback(loadFresh)
  const loadMoreHandler = useStableCallback(loadMore)
  const value = useMemo(
    () => ({
      registerComponent,
      hasMore,
      loading,
      loadingMore,
      loadFresh: loadFreshHandler,
      loadMore: loadMoreHandler,
      status,
      planningRecords,
      error,
    }),
    [
      registerComponent,
      hasMore,
      loading,
      loadingMore,
      loadFreshHandler,
      loadMoreHandler,
      status,
      planningRecords,
      error,
    ],
  )
  return <PlanningRecordsContext.Provider value={value}>{children}</PlanningRecordsContext.Provider>
}

export { usePlanningRecords, usePlanningRecordsPreview, PlanningRecordsProvider }
export default PlanningRecordsProvider
